import React, { useLayoutEffect, useRef, useState } from "react";

const SMART_SPEED = 250;

/**
 * React stand-in for the reference's Owl Carousel 2.3.4 in its default (fixed item width) mode, as the
 * Andar Bahar (ab20: rtl, margin 0, slideBy 4) and Andar Bahar 2 (abj: rtl, margin 10, slideBy 4) video
 * sliders use it. Renders Owl's initialised markup so the ported CSS applies:
 *   div#id.{className}.owl-rtl.owl-loaded.owl-drag
 *     > .owl-stage-outer > .owl-stage[style transform/transition/width]
 *       > .owl-item[.active][style width (+ margin-left when margin)] × n
 *     + .owl-nav > button.owl-prev + button.owl-next   + .owl-dots.disabled
 * Owl maths: width() = element width + margin, item width = (width() / items).toFixed(3) − margin (jQuery
 * clamps a negative css width to 0), coordinates accumulate item width + margin, stage width = ceil(last coordinate),
 * non-loop maximum = count − items, `.active` = items whose start or end lies inside the viewport.
 * No slides → the reference's `replace.owl.carousel "<div/>"`: one item holding an empty div.
 * `resetKey`: a change (new cards) jumps back to position 0 without animation.
 */
export default function AbGridOwl({ id, className, items = 4, margin = 0, slideBy = 4, resetKey, children }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  const [pos, setPos] = useState({ current: 0, speed: 0, key: resetKey });
  const slides = React.Children.toArray(children);
  const list = slides.length ? slides : [<div key="empty" />];
  const count = list.length;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // jQuery width(): the computed content width
    const measure = () => setWidth(parseFloat(getComputedStyle(el).width) || 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  let { current, speed } = pos;
  if (pos.key !== resetKey) {
    current = 0;
    speed = 0;
    setPos({ current: 0, speed: 0, key: resetKey });
  }

  // Owl's width() is the element width + margin; item width = (width() / items).toFixed(3) − margin
  const owlWidth = width + margin;
  const rawItem = Number((owlWidth / items).toFixed(3)) - margin;
  const cssItem = Math.max(0, rawItem);
  const coords = [];
  for (let i = 0; i < count; i++) coords.push((coords[i - 1] || 0) + (rawItem + margin));
  const maximum = Math.max(0, count - items);
  current = Math.min(current, maximum);
  const coordinate = current > 0 ? Math.ceil(coords[current - 1] || 0) : 0;
  const stageWidth = Math.ceil(Math.abs(coords[count - 1] || 0));

  const go = (next) => {
    const target = Math.max(0, Math.min(maximum, current + (next ? slideBy : -slideBy)));
    if (target === current) return;
    const s = Math.min(Math.max(Math.abs(target - current), 1), 6) * SMART_SPEED;
    setPos({ current: target, speed: s, key: resetKey });
  };

  // Owl (rtl): begin = coordinate, end = begin + width(); an item is active when its start or end lies inside
  const begin = coordinate;
  const end = begin + owlWidth;
  const isActive = (i) => {
    if (!slides.length) return true;
    if (!width) return false;
    const inner = coords[i - 1] || 0;
    const outer = Math.abs(coords[i]);
    return (inner >= begin && inner < end) || (outer > begin && outer < end);
  };

  const itemStyle = margin ? { width: `${cssItem}px`, marginLeft: `${margin}px` } : { width: `${cssItem}px` };

  return (
    <div ref={ref} id={id} className={`${className} owl-rtl owl-loaded owl-drag`}>
      <div className="owl-stage-outer">
        <div
          className="owl-stage"
          style={{
            transform: `translate3d(${coordinate}px, 0px, 0px)`,
            transition: `${speed / 1000}s`,
            width: `${stageWidth}px`,
          }}
        >
          {list.map((slide, i) => (
            <div key={i} className={isActive(i) ? "owl-item active" : "owl-item"} style={itemStyle}>
              {slide}
            </div>
          ))}
        </div>
      </div>
      <div className={count <= items ? "owl-nav disabled" : "owl-nav"}>
        <button type="button" role="presentation" className={current <= 0 ? "owl-prev disabled" : "owl-prev"} onClick={() => go(false)}>
          <span aria-label="Previous">‹</span>
        </button>
        <button
          type="button"
          role="presentation"
          className={current >= maximum ? "owl-next disabled" : "owl-next"}
          onClick={() => go(true)}
        >
          <span aria-label="Next">›</span>
        </button>
      </div>
      <div className="owl-dots disabled" />
    </div>
  );
}
